import React, { useState } from 'react';
import Drawer from 'react-modern-drawer';
import { useStravaTranslations } from '@strava/container-context';
import { Alert } from '@strava/ui/Alert';
import Button from '@strava/ui/Button';
import ActionsCancelNormalSmall from '@strava/icons/ActionsCancelNormalSmall';
import {
  DisclaimerText,
  AccountAuthButton,
  elemAttrForAnalytics,
  APPLE,
  EMAIL,
  FACEBOOK,
  GOOGLE,
  EmailRegistrationForm,
  AnalyticsFieldsTypes,
  AlertTypes,
  EmailRegistrationFormProps,
  OrDivider
} from '@strava/authentication-ui';
import { isEmpty } from 'lodash-es';
import VisuallyHidden from '@strava/ui/VisuallyHidden';
import styles from './MobileRegistrationOptions.module.scss';

type MobileRegistrationOptionsProps = EmailRegistrationFormProps & {
  track: (fields: AnalyticsFieldsTypes) => void;
  emailAuthorizationUrl: string;
  handleExtAuthBtnClick: (
    option: typeof FACEBOOK | typeof GOOGLE | typeof APPLE
  ) => void;
  cohort?: string | undefined;
  flashMessage?: string | undefined;
  showUpdatedMobileStyling?: boolean;
};

export const MobileRegistrationOptions = ({
  emailAuthorizationUrl,
  handleExtAuthBtnClick,
  track,
  flashMessage,
  showUpdatedMobileStyling = false,
  ...emailRegFormProps
}: MobileRegistrationOptionsProps) => {
  const [alert, setAlert] = useState<AlertTypes>(
    flashMessage ? { severity: 'error', message: flashMessage } : null
  );
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const { t } = useStravaTranslations();

  const i18nPrefix = 'account_registration_page_ui';

  const handleEmailRegistration = () => {
    const element = elemAttrForAnalytics(EMAIL);
    track({
      action: 'click',
      element
    });

    setIsDrawerOpen(true);
  };

  return (
    <>
      {!isEmpty(alert) && (
        <Alert hideCloseButton={true} alertType={alert.severity}>
          {alert.message}
        </Alert>
      )}

      <div data-testid="auth-btns">
        <AccountAuthButton
          variant="facebook"
          onClick={() => handleExtAuthBtnClick(FACEBOOK)}
          data-testid="facebook_auth_btn"
        >
          {t(`${i18nPrefix}.signup_ctas.facebook_cta`)}
        </AccountAuthButton>

        <AccountAuthButton
          variant="google"
          onClick={() => handleExtAuthBtnClick(GOOGLE)}
          data-testid="google_auth_btn"
        >
          {t(`${i18nPrefix}.signup_ctas.google_cta`)}
        </AccountAuthButton>

        <AccountAuthButton
          variant="apple"
          onClick={() => handleExtAuthBtnClick(APPLE)}
          data-testid="apple_auth_btn"
        >
          {t(`${i18nPrefix}.signup_ctas.apple_cta`)}
        </AccountAuthButton>

        {!showUpdatedMobileStyling && (
          <AccountAuthButton
            variant="email"
            onClick={handleEmailRegistration}
            data-testid="email_auth_btn"
          >
            {t(`${i18nPrefix}.signup_ctas.email_cta`)}
          </AccountAuthButton>
        )}

        {showUpdatedMobileStyling && (
          <>
            <OrDivider
              text={t(`${i18nPrefix}.or_sign_up_with_email`)}
              theme="light"
            />
            <EmailRegistrationForm
              authorizationUrl={emailAuthorizationUrl}
              setAlert={setAlert}
              track={track}
              theme="light"
              idPrefix="mobile"
              {...emailRegFormProps}
            />
          </>
        )}

        <DisclaimerText i18nPrefix={i18nPrefix} />
      </div>

      {!showUpdatedMobileStyling && (
        <Drawer
          open={isDrawerOpen}
          direction="bottom"
          size="100%"
          className={styles.emailRegDrawer}
        >
          <div className={styles.drawerHeader}>
            <h2>{t(`${i18nPrefix}.mobile.sign_up`)}</h2>
            <Button
              variant="icon"
              type="button"
              onClick={() => setIsDrawerOpen(false)}
            >
              <VisuallyHidden>{t(`${i18nPrefix}.close`)}</VisuallyHidden>
              <ActionsCancelNormalSmall />
            </Button>
          </div>
          <EmailRegistrationForm
            authorizationUrl={emailAuthorizationUrl}
            setAlert={setAlert}
            track={track}
            theme="light"
            idPrefix="mobile"
            {...emailRegFormProps}
          />
          <DisclaimerText i18nPrefix={i18nPrefix} />
        </Drawer>
      )}
    </>
  );
};

export default MobileRegistrationOptions;
